import React, { useCallback, useMemo, useState } from "react";
import { CategoricMeasurementRequirement, FailureMeasurementResult, isCategoricMeasurementRequirement, isLineMeasurement, isLineMeasurementRequirement, MeasurementKey, MeasurementRequirement, MeasurementResult, NumericMeasurementResult } from "../../data/Measurements/Measurement";
import { getMetadataForMeasurement, getMetadataForResult, TaskData } from "../../data/Tasks/Task";
import Immutable from "immutable";
import { EKG_LEAD_ORDER } from "../../data/Utils";
import { useMeasurement } from "../../providers/NewMeasurementProvider";
import { useCanvasViewport } from "./CanvasViewportContext";

const RADIO_BUTTON_THRESHOLD = 4;

export interface SidebarProps {
  /**
   * The task data.
   */
  taskData: TaskData;

  /**
   * The height of the sidebar.
   */
  height: number;
}

/**
 * Sidebar component.
 */
export const Sidebar: React.FC<SidebarProps> = ({ taskData }) => {
  const height = 720;

  const { nextMeasurement, results } = useMeasurement();

  // MARK: UI

  return (
    <div>
      <div className="z-200 absolute top-1/2 -translate-y-1/2 -right-0 w-64 m-6 flex flex-col select-none" style={{ height: `${height}px` }}>
        {/* Top Panel */}

        {nextMeasurement && isLineMeasurementRequirement(nextMeasurement) && <NextMeasurementPanel nextMeasurement={nextMeasurement} />}
        {nextMeasurement && isCategoricMeasurementRequirement(nextMeasurement) && <CategoricMeasurementPanel measurementRequirement={nextMeasurement} />}

        {/* Bottom Panel */}
        <BottomPanel results={results} taskData={taskData} />
      </div>
    </div>
  )
}

const CategoricMeasurementPanel = ({
  measurementRequirement
}: {
  measurementRequirement: CategoricMeasurementRequirement,
}) => {
  const { metadata, options } = measurementRequirement;
  const [selectedValue, setSelectedValue] = useState<string>("");
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);

  const { updateMeasurementAsync } = useMeasurement();

  const handleValueChange = (value: string) => {
    setSelectedValue(value);
    setIsDropdownOpen(false);

    updateMeasurementAsync({
      type: 'categoric',
      key: measurementRequirement.key,
      value,
      drawable: false,
    })
      .then(() => {
        setSelectedValue("");
      });
  };

  const renderRadioButtons = () => (
    <div className="flex flex-col space-y-2">
      {options.map((option) => (
        <label key={option} className="flex items-center space-x-2 cursor-pointer">
          <input
            type="radio"
            value={option}
            checked={selectedValue === option}
            onChange={(e) => handleValueChange(e.target.value)}
            className="w-4 h-4 text-indigo-600 border-gray-300 focus:ring-indigo-500"
          />
          <span className="text-sm text-gray-700">{option}</span>
        </label>
      ))}
    </div>
  );

  const renderDropdown = () => (
    <div className="relative">
      <button
        type="button"
        className="w-full px-4 py-2 text-left text-sm bg-white border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-1 focus:ring-indigo-500 focus:border-indigo-500"
        onClick={() => setIsDropdownOpen(!isDropdownOpen)}
      >
        {selectedValue || "Select an option"}
        <span className="absolute inset-y-0 right-0 flex items-center pr-2 pointer-events-none">
          <svg
            className="h-5 w-5 text-gray-400"
            xmlns="http://www.w3.org/2000/svg"
            viewBox="0 0 20 20"
            fill="currentColor"
            aria-hidden="true"
          >
            <path
              fillRule="evenodd"
              d="M5.293 7.293a1 1 0 011.414 0L10 10.586l3.293-3.293a1 1 0 111.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414z"
              clipRule="evenodd"
            />
          </svg>
        </span>
      </button>

      {isDropdownOpen && (
        <div className="absolute z-10 mt-1 w-full bg-white shadow-lg max-h-60 rounded-md py-1 ring-1 ring-black ring-opacity-5 overflow-auto focus:outline-none">
          {options.map((option) => (
            <div
              key={option}
              className={`${selectedValue === option ? 'bg-indigo-100 text-indigo-900' : 'text-gray-900'
                } cursor-pointer select-none relative py-2 pl-3 pr-9 text-sm hover:bg-indigo-50`}
              onClick={() => handleValueChange(option)}
            >
              {option}
              {selectedValue === option && (
                <span className="absolute inset-y-0 right-0 flex items-center pr-4 text-indigo-600">
                  <svg className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
                    <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                  </svg>
                </span>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );

  return (
    <div className="flex-1 bg-white bg-opacity-30 backdrop-filter backdrop-blur-lg rounded-lg shadow-md m-2 p-4 border border-gray-400" style={{ height: '30%' }}>
      <div className="flex flex-col h-full justify-between">
        <div>
          <div className="flex items-center space-x-2">
            <div className="text-indigo-600 text-sm rounded-full bg-indigo-100 px-2 py-1 inline-block">
              Category selection
            </div>
            {metadata?.lead && (
              <div className="text-xs text-gray-700 rounded-full bg-gray-200 px-2 py-1 inline-block">
                {metadata.lead}
              </div>
            )}
          </div>
          <div className="text-2xl font-bold text-gray-800 mt-2">{metadata.name}</div>
          <div className="text-base text-gray-600 mt-2">{metadata.description}</div>

          <div className="mt-4">
            {options.length <= RADIO_BUTTON_THRESHOLD ? renderRadioButtons() : renderDropdown()}
          </div>
        </div>

        <button
          className="mt-4 bg-red-500 text-white text-sm font-semibold py-2 px-4 rounded-lg hover:bg-red-600 focus:outline-none focus:ring-2 focus:ring-red-500 focus:ring-opacity-50"

        >
          Unmeasurable
        </button>
      </div>
    </div>
  );
};

const NextMeasurementPanel = ({ nextMeasurement }: { nextMeasurement: MeasurementRequirement }) => {
  const unmeasurableText = nextMeasurement?.key === 'calibration' ? "Can't calibrate" : 'Unmeasurable';

  const { updateMeasurement } = useMeasurement();

  const onMeasurementUnmeasurable = useCallback(() => {
    updateMeasurement({
      type: 'unmeasurable',
      key: nextMeasurement.key,
      drawable: false,
    })
  }, [nextMeasurement, updateMeasurement]);

  return (
    <div className="flex-1 bg-white bg-opacity-30 backdrop-filter backdrop-blur-lg rounded-lg shadow-md m-2 p-4 border border-gray-400" style={{ height: '30%' }}>
      {
        nextMeasurement === undefined && <div className="text-gray-600 text-sm">No measurements remaining</div>
      }
      {
        nextMeasurement !== undefined &&
        <div className="flex flex-col h-full justify-between">
          <div>
            <div className="flex items-center space-x-2">
              <div className="text-indigo-600 text-sm rounded-full bg-indigo-100 px-2 py-1 inline-block">Next measurement</div>
              {nextMeasurement.metadata?.lead && <div className="text-xs text-gray-700 rounded-full bg-gray-200 px-2 py-1 inline-block">{nextMeasurement.metadata?.lead ?? 'Any'}</div>}
            </div>
            <div className="text-2xl font-bold text-gray-800 mt-2">{nextMeasurement.metadata.name}</div>
            <div className="text-base text-gray-600 mt-2">{nextMeasurement.metadata.description}</div>
          </div>
          <button
            className="mt-4 bg-red-500 text-white text-sm font-semibold py-2 px-4 rounded-lg hover:bg-red-600 focus:outline-none focus:ring-2 focus:ring-red-500 focus:ring-opacity-50"
            onClick={onMeasurementUnmeasurable}>
            {unmeasurableText}
          </button>
        </div>
      }
    </div>
  )
}

const BottomPanel = ({ results, taskData }: { results: MeasurementResult[], taskData: TaskData }) => {
  const resultsGroupedByLead = useMemo(() => {
    const grouped: Record<string, MeasurementResult[]> = {};

    results.forEach(res => {
      const metadata = getMetadataForResult(res, taskData)!;

      grouped[metadata.lead] = grouped[metadata.lead] ?? [];
      grouped[metadata.lead].push(res);
    });

    return Immutable.Map(grouped);
  }, [results, taskData]);

  // order entries
  const orderedResultsGroupedByLead = resultsGroupedByLead.sortBy((_, lead) => EKG_LEAD_ORDER.indexOf(lead));

  return (
    <div className="flex-3 bg-white bg-opacity-30 backdrop-filter backdrop-blur-lg rounded-lg shadow-md m-2 p-4 border border-gray-400 max-h-[75%] overflow-y-scroll custom-scrollbar">
      <div className="text-gray-800 text-lg font-semibold">Measurements</div>

      {
        orderedResultsGroupedByLead.entrySeq().map(([lead, results]) => (
          <div key={lead} className="mt-4">
            <BottomPanelLeadLabelGroup lead={lead} results={results} taskData={taskData} />
          </div>
        ))
      }
    </div>
  )
}


const BottomPanelLeadLabelGroup: React.FC<{ lead: string, results: MeasurementResult[], taskData: TaskData }> = ({ lead, results, taskData }) => {
  const numericResults = results.filter(r => r.type === 'numeric');
  const errorResults = results.filter(r => r.type === 'failure');

  const numericResultsOrderedByMeasurementType = numericResults.sort((a, b) => {
    return a.measurementType === 'derived' ? 1 : -1;
  });

  return (
    <>
      <div className="text-indigo-600 text-xs rounded-full bg-indigo-100 px-2 py-1 inline-block">{lead}</div>
      <div className="text-gray-600 text-sm mt-2">
        {
          numericResultsOrderedByMeasurementType.map(res => (
            <SuccessResultLabel key={res.key} result={res as NumericMeasurementResult} taskData={taskData} />
          ))
        }
      </div>
      <div className="text-gray-600 text-sm mt-2">
        {
          errorResults.map(res => (
            <FailureResultLabel key={res.key} result={res as FailureMeasurementResult} taskData={taskData} />
          ))
        }
      </div>
    </>
  )
}

const SuccessResultLabel: React.FC<{ result: NumericMeasurementResult, taskData: TaskData }> = ({ result, taskData }) => {
  const isDimmed = false;
  const { name, unit } = getMetadataForResult(result, taskData)!;

  const unitStr = unit === 'unitless' ? '' : unit;

  const { setFocusedKeys, focusedKeys, fitToMeasurement } = useCanvasViewport();
  const { measurements } = useMeasurement();

  const setFocus = useCallback(() => {
    if (result.measurementType !== 'line') {
      return;
    }

    setFocusedKeys([result.key]);
  }, [result, setFocusedKeys]);

  const removeFocus = useCallback(() => {
    setFocusedKeys(null);
  }, [setFocusedKeys]);

  const zoomInToMeasurement = useCallback(() => {
    const rawMeasurement = measurements.get(result.key);

    if (!rawMeasurement || !isLineMeasurement(rawMeasurement)) {
      return;
    }

    fitToMeasurement(rawMeasurement);
  }, [result, measurements, fitToMeasurement]);

  const isFocused = useMemo(() => focusedKeys?.includes(result.key), [focusedKeys, result.key]);

  return (
    <div
      className={`cursor-pointer flex items-center space-x-2 ${isDimmed ? 'opacity-50' : ''} ${isFocused ? 'bg-indigo-100' : ''} rounded-md`}
      onMouseEnter={setFocus}
      onMouseLeave={removeFocus}
      onClick={zoomInToMeasurement}
    >
      <div className="text-gray-800 font-semibold">{name}</div>
      <div className={`flex-grow border-t mx-2 ${isDimmed ? 'border-gray-400' : 'border-indigo-600'}`} style={{ height: '2px', alignSelf: 'center' }}></div>
      {<div className="text-gray-600">{result.value.toFixed(1)} <small>{unitStr}</small></div>}
    </div>
  )
}

const FailureResultLabel: React.FC<{ result: FailureMeasurementResult, taskData: TaskData }> = ({ result, taskData }) => {
  const isDimmed = true;
  const { name } = getMetadataForResult(result, taskData)!;

  return (
    <div className={`flex items-center space-x-2 ${isDimmed ? 'opacity-50' : ''}`}>
      <div className="text-gray-800 font-semibold">{name}</div>
      <div className={`flex-grow border-t mx-2 ${isDimmed ? 'border-gray-400' : 'border-indigo-600'}`} style={{ height: '2px', alignSelf: 'center' }}></div>
    </div>
  );
};
